import type * as React from "react";
import { cn } from "../../../lib/cn";
import { CitationHighlighter } from "../CitationHighlighter";
import type { CitationAnchor } from "../types";

/**
 * Payload shape consumed by `PDFViewer`.
 */
export interface PDFPayload {
  /** Direct URL to the PDF (signed MinIO URL is fine). */
  src: string;
  /** Total page count (used for the page label "page N of M"). */
  pageCount?: number;
}

export interface PDFViewerProps {
  payload: PDFPayload;
  anchor?: CitationAnchor;
}

/**
 * PDFViewer — placeholder iframe + on-top citation overlay.
 *
 * Scaffolding: uses `<iframe src="…#page=N">` (PDF.js URL fragment) to
 * scroll to the cited page on mount. A `CitationHighlighter` floats above
 * the iframe at the `anchor.bbox` coordinates (`[x, y, w, h]` in % of
 * viewport) when one is provided.
 *
 * Production swap: when the feature ships PDF.js or pdfium-rs, this file
 * is the *only* place that changes — every consumer keeps the same props.
 */
export const PDFViewer: React.FC<PDFViewerProps> = ({ payload, anchor }) => {
  const page = anchor?.page ?? 1;
  // PDF URL fragment per Adobe spec — works in Chrome / Edge / Firefox.
  const src = `${payload.src}#page=${page}&zoom=page-width`;

  const bbox = anchor?.bbox;
  const overlay = bbox
    ? {
        left: `${bbox[0]}%`,
        top: `${bbox[1]}%`,
        width: `${bbox[2]}%`,
        height: `${bbox[3]}%`,
      }
    : null;

  return (
    <div className="relative h-full min-h-[320px] w-full overflow-hidden rounded-lg border border-hairline bg-canvas">
      <iframe
        title="PDF source"
        src={src}
        className="absolute inset-0 h-full w-full"
        // Sandbox is loose enough for PDF rendering but blocks scripts injected
        // by malicious source PDFs — defence in depth above rehype-sanitize.
        sandbox="allow-same-origin allow-scripts allow-popups"
      />
      {overlay ? (
        <CitationHighlighter
          className={cn(
            "pointer-events-none absolute z-10",
            "ring-2 ring-ando-orange/70",
          )}
          style={overlay}
          aria-hidden
        >
          <span className="sr-only">Zona citata</span>
        </CitationHighlighter>
      ) : null}
      {payload.pageCount ? (
        <div
          className={cn(
            "absolute right-2 bottom-2 z-20 rounded-full",
            "bg-ink-1/85 px-2 py-0.5 font-medium text-[10px] text-cream",
          )}
        >
          pag. {page}
          {payload.pageCount ? ` / ${payload.pageCount}` : null}
        </div>
      ) : null}
    </div>
  );
};
PDFViewer.displayName = "PDFViewer";
